// src/core/UniqodeInsightsClient.ts
import { IUniqodeInsightsClient } from '../types/sdk';
import { IPlugin } from '../types/plugin';
import { SdkConfig } from '../types/config';
import { QueuedEvent } from '../types';
import { PluginManager } from './pluginManager';
import { ITransport } from '../types/transport';
import { DefaultTransport } from './transports/DefaultTransport';
import { ENV_CONFIG } from '../env_config';
import { Logger, ILogger, LogLevel } from '../utils/logger';

export class UniqodeInsightsClient implements IUniqodeInsightsClient {
  private config?: SdkConfig;
  private pluginManager!: PluginManager;
  private transport!: ITransport[];
  private eventIndexCounter: number = 0;
  private logger!: ILogger;
  private _isInitialized: boolean = false;
  private _pendingEventQueue: QueuedEvent[] = [];
  private _currentUserId?: string;
  private _preInitOptOutState?: boolean;

  constructor() {
    this.pluginManager = new PluginManager(this);
  }

  private _isOptedOut(): boolean {
    if (this._isInitialized && this.config) {
      return !!this.config.optOut; // Use !! to ensure boolean
    }
    return !!this._preInitOptOutState; // Use !! to ensure boolean
  }

  // Method to process an event through plugins and send it via transport
  private _processAndSendEvent(event: QueuedEvent): void {
    this.logger?.debug(`Processing event "${event.eventType}" (Index: ${event.index ?? 'N/A'})`);
    if (!this.config) {
      this.logger?.warn(`SDK not initialized - dropping event "${event.eventType}"`);
      return;
    }

    if (this._isOptedOut()) {
      this.logger?.debug(`Opt-out active - skipping event "${event.eventType}"`);
      return;
    }

    const eventIdForLog = `"${event.eventType}" (Index: ${event.index ?? 'N/A'})`;
    this.logger?.debug(`Processing event ${eventIdForLog}`);

    let processedEvent: QueuedEvent | null = null;
    try {
      processedEvent = this.pluginManager.processEventThroughPlugins(event);
    } catch (error) {
      this.logger?.error(`Plugin processing error for event ${eventIdForLog}:`, error);
      return;
    }

    if (processedEvent) {
      const processedEventIdForLog = `"${processedEvent.eventType}" (Index: ${processedEvent.index ?? 'N/A'})`;
      this.logger?.debug(`Event processed by plugins: ${processedEventIdForLog}`);

      if (this.transport) {
        let transportSuccess = false;
        let transportError: Error | undefined;

        try {
          this.logger?.debug(`Sending event ${processedEventIdForLog} to transport`);
          for (const transport of this.transport) {
            transport.send(processedEvent).catch(error => {
              this.logger?.error(
                `Error sending event ${processedEventIdForLog} via transport:`,
                error
              );
            });
            this.logger?.debug(`Event ${processedEventIdForLog} sent successfully via transport`);
          }
          transportSuccess = true;
          this.logger?.debug(`Event ${processedEventIdForLog} sent successfully`);
        } catch (error) {
          transportSuccess = false;
          transportError = error instanceof Error ? error : new Error(String(error));
          this.logger?.error(`Transport error for event ${processedEventIdForLog}:`, error);
          // TODO: Implement retry logic or dead-letter queue for transport errors.
        }

        // Process post-transport plugins
        try {
          this.pluginManager.processEventThroughPostTransportPlugins({
            success: transportSuccess,
            error: transportError,
            event: processedEvent,
          });
        } catch (error) {
          this.logger?.error(
            `Post-transport plugin error for event ${processedEventIdForLog}:`,
            error
          );
        }
      } else {
        this.logger?.error(`Transport not available for event ${processedEventIdForLog}`);
      }
    } else {
      this.logger?.debug(`Event ${eventIdForLog} dropped by plugin pipeline`);
    }
  }

  init(config: SdkConfig): void {
    const finalOptOut = config.optOut ?? false;

    this.logger =
      config.logger ??
      new Logger(config.logLevel ?? (ENV_CONFIG.LOG_LEVEL.toLowerCase() as LogLevel) ?? 'warn');

    const mergedConfig: SdkConfig = {
      ...config,
      clientKey: config.clientKey,
      optOut: finalOptOut,
      apiEndpoint: config.apiEndpoint ?? ENV_CONFIG.API_ENDPOINT,
      logLevel: config.logLevel ?? (ENV_CONFIG.LOG_LEVEL.toLowerCase() as LogLevel),
      logger: this.logger, // Always provide the logger instance
    };

    if (this._isInitialized) {
      this.logger.warn('SDK instance already initialized. Re-initializing...');
      if (this.pluginManager) {
        this.pluginManager.destroyAllPlugins();
      }
    }

    this.config = mergedConfig;
    this.pluginManager.setSdkConfig(mergedConfig);

    // When instantiating PluginManager and DefaultTransport, pass the mergedConfig

    if (mergedConfig.transport) {
      this.transport = mergedConfig.transport;
      this.logger.debug('Using provided custom transport');
    } else {
      this.transport = [
        new DefaultTransport(
          mergedConfig.clientKey,
          mergedConfig?.transportConfig?.at(0),
          this.logger
        ),
      ];
      this.logger.debug('Using DefaultTransport');
    }

    this.pluginManager.initializeAllPlugins();
    this._isInitialized = true;
    this.logger.info('SDK initialized successfully');

    if (this._pendingEventQueue.length > 0) {
      this.logger.debug(`Processing ${this._pendingEventQueue.length} pending events`);
      const eventsToProcess = [...this._pendingEventQueue];
      this._pendingEventQueue = [];

      for (const pendingEvent of eventsToProcess) {
        this.eventIndexCounter += 1;
        const fullQueuedEvent: QueuedEvent = {
          ...pendingEvent,
          index: this.eventIndexCounter,
        };
        this._processAndSendEvent(fullQueuedEvent);
      }
      this.logger.debug('Pre-initialization pending event queue processed');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  track(eventType: string, eventProperties?: Record<string, any>): void {
    if (!this._isInitialized) {
      const pendingEvent: QueuedEvent = {
        eventType,
        eventProperties,
        timestamp: Date.now(),
        // index is intentionally omitted for pending events
        // Add system properties that are available pre-init
        userId: this._currentUserId,
      };
      this._pendingEventQueue.push(pendingEvent);
      this.logger?.debug(
        `Event "${eventType}" queued (pending init). Queue size: ${this._pendingEventQueue.length}`
      );
      return;
    }

    // SDK is initialized, proceed to process the event immediately
    this.eventIndexCounter += 1;

    const queuedEvent: QueuedEvent = {
      eventType,
      eventProperties,
      timestamp: Date.now(),
      index: this.eventIndexCounter,
      // Add system properties
      userId: this._currentUserId,
    };

    this.logger?.debug(`Event "${eventType}" (Index: ${queuedEvent.index}) received`);
    this._processAndSendEvent(queuedEvent);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  identify(userId: string, userProperties?: Record<string, any>): void {
    // Store the userId for future events
    this._currentUserId = userId;

    const eventType = '$identify'; // Standard event name for identification
    // Only include user properties in eventProperties, userId will be at top level
    const eventProps = userProperties ?? {};

    this.logger?.debug(`Identifying user: ${userId}`);

    // Delegate to the track method for consistent event handling
    this.track(eventType, eventProps);
  }

  optOut(isOptedOut: boolean): void {
    if (this._isInitialized && this.config) {
      this.config.optOut = isOptedOut;
    } else {
      // SDK not initialized, store in pre-init state
      this._preInitOptOutState = isOptedOut;
      // Log a slightly different message if not initialized
      const status = isOptedOut ? 'enabled (pre-initialization)' : 'disabled (pre-initialization)';
      this.logger?.debug(`Opt-out status set to ${status}`);
      // No further action like clearing queue if not initialized
    }
  }

  /**
   * Start all plugins. Useful for resuming plugin operations after stopping them.
   */
  startPlugins(): void {
    if (!this._isInitialized) {
      this.logger?.warn('Cannot start plugins - SDK not initialized');
      return;
    }

    if (this.pluginManager) {
      this.pluginManager.startAllPlugins();
    }
  }

  /**
   * Stop all plugins. This pauses plugin operations without destroying them.
   */
  stopPlugins(): void {
    if (!this._isInitialized) {
      this.logger?.warn('Cannot stop plugins - SDK not initialized');
      return;
    }

    if (this.pluginManager) {
      this.pluginManager.stopAllPlugins();
    }
  }

  async shutdown(): Promise<void> {
    this.logger?.info('SDK shutting down');

    if (!this._isInitialized && !this.config && !this.pluginManager) {
      this.logger?.debug('Instance already shut down or never initialized');
      return;
    }
    for (const transport of this.transport) {
      if (typeof transport.shutdown === 'function') {
        try {
          this.logger?.debug('Shutting down transport');
          await transport.shutdown();
        } catch (error) {
          this.logger?.error('Error during transport shutdown:', error);
        }
      }
    }

    // 2. Clean up plugins
    if (this.pluginManager) {
      this.pluginManager.destroyAllPlugins();
      // @ts-ignore
      this.pluginManager = undefined;
    }

    // 3. Clear transport reference
    // @ts-ignore
    this.transport = undefined;

    this._pendingEventQueue = [];
    this.eventIndexCounter = 0;
    this._isInitialized = false;
    this.config = undefined;
    this.logger?.info('SDK shutdown complete');
  }

  getUserId(): string | null {
    // Implementation depends on how/where userId is stored
    return null;
  }

  getAnonymousId(): string | null {
    // Implementation depends on how/where anonymousId is stored/generated
    return null;
  }

  addPlugin(plugin: IPlugin): void {
    const addedPlugin = this.pluginManager.add(plugin);

    // If the plugin was successfully added by the manager AND the SDK is already initialized,
    // then initialize this newly added plugin immediately.
    if (addedPlugin && this.config) {
      this.logger?.debug(`Initializing plugin "${addedPlugin.name}" (post-init)`);
      this.pluginManager.initializePlugin(addedPlugin);
    } else if (addedPlugin) {
      this.logger?.debug(`Plugin "${addedPlugin.name}" added (will initialize on SDK init)`);
    }
    // If addedPlugin is undefined, it means the PluginManager handled the case (e.g., duplicate) and logged it.
  }

  removePlugin(pluginName: string): boolean {
    return this.pluginManager.remove(pluginName); // Delegate to PluginManager
  }

  getPlugin(pluginName: string): IPlugin | undefined {
    return this.pluginManager.get(pluginName); // Delegate to PluginManager
  }

  listPlugins(): IPlugin[] {
    const allPlugins = this.pluginManager.getAll();
    return [
      ...allPlugins.enrichment,
      ...allPlugins.preTransport,
      ...allPlugins.postTransport,
      ...allPlugins.standalone,
    ];
  }

  public async flush(): Promise<void> {
    if (!this._isInitialized) {
      this.logger?.warn('SDK not initialized. Cannot flush');
      return;
    }

    this.logger?.debug('Manual flush triggered');
    for (const transport of this.transport) {
      if (typeof transport.flush === 'function') {
        try {
          this.logger?.debug('Flushing transport');
          await transport.flush();
        } catch (error) {
          this.logger?.error('Error during transport flush:', error);
        }
      } else {
        this.logger?.warn(
          `Transport "${transport.name}" does not support flush operation, skipping`
        );
      }
    }
  }
}
